import React, { useCallback, useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./header.css";
import { BsCart3, BsPersonCircle } from "react-icons/bs";
import { getLogo } from "../../services/HeaderService";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Search, X } from "lucide-react";
import SearchContent from "./SearchBar";
import { getToken } from "../../utils/getToken";
import CartDrawer from "./CartDrawer";
import { useSyncedCart } from "../../hooks/useSyncedCart";
import { CART_DRAWER_OPEN_EVENT } from "../../utils/cartDrawerEvents";

const CartCount = ({ count }) => {
  if (!count) return null;
  return (
    <div
      className="absolute inline-flex items-center justify-center w-5 h-5 font-bold text-white bg-red-500 border-2 border-white rounded-full -top-2 -right-2"
      style={{ fontSize: "10px" }}
    >
      {count}
    </div>
  );
};

const AccountProfileLink = ({ className = "", onClick, size = 28 }) => (
  <Link
    to="/profile"
    className={`kuku-icon-btn kuku-account-link ${className}`.trim()}
    onClick={onClick}
    aria-label="Profile"
  >
    <img
      src="/kuku-assets/images/admin-placeholder.png"
      alt=""
      className="kuku-account-avatar"
      style={{ width: size, height: size }}
    />
  </Link>
);

const HeaderData = () => {
  const { pathname } = useLocation();
  const [click, setClick] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [searchContent, setSearchContent] = useState(null);
  const [searchBarShow, setSearchBarShow] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const [authToken, setAuthToken] = useState(() => getToken());
  const localStore = authToken;
  const {
    cartCount,
    items: cartItems,
    refetchCart,
    removeProduct,
    totalAmount,
    updateQuantity,
  } = useSyncedCart({ enabled: Boolean(localStore) });

  const toggleSearch = () => {
    setClick(false);
    setIsCartOpen(false);
    setSearchBarShow((old) => !old);
    setSearchContent(null);
  };

  const onChange = (e) => {
    setSearchContent(e.target.value);
  };

  const { data: logoData } = useQuery({
    queryKey: ["logo"],
    queryFn: getLogo,
  });

  const closeAll = () => {
    setClick(false);
    setIsAboutOpen(false);
    setSearchBarShow(false);
    setSearchContent(null);
    setIsCartOpen(false);
  };

  const handleProfileClick = () => {
    closeAll();

    window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, 0);
  };

  useEffect(() => {
    const refreshAuthToken = () => {
      setAuthToken(getToken());
    };

    window.addEventListener("login-modal-authenticated", refreshAuthToken);
    window.addEventListener("auth-state-changed", refreshAuthToken);
    window.addEventListener("storage", refreshAuthToken);

    return () => {
      window.removeEventListener("login-modal-authenticated", refreshAuthToken);
      window.removeEventListener("auth-state-changed", refreshAuthToken);
      window.removeEventListener("storage", refreshAuthToken);
    };
  }, []);
  const openCart = useCallback(() => {
    setClick(false);
    setIsAboutOpen(false);
    setSearchBarShow(false);
    setSearchContent(null);

    const currentToken = getToken();
    if (currentToken !== authToken) {
      setAuthToken(currentToken);
    }

    if (currentToken) {
      refetchCart();
    }

    setIsCartOpen(true);
  }, [authToken, refetchCart]);

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const drawerItems = cartItems.map((item) => ({
    id: item.product_id,
    name: item.product_name,
    packSize: item.weight ? `Order of ${item.weight}` : "Kuku Namkeen",
    price: Number(item.price || 0),
    originalPrice: Number(item.originalPrice || item.original_price || 0),
    quantity: Number(item.quantity || 1),
    image:
      item.thumbnail_image ||
      "/kuku-assets/products/dry-chapti-kachori/1 chapti kachori.jpg",
  }));

  const handleIncrement = async (itemId) => {
    const item = cartItems.find(
      (cartItem) => String(cartItem.product_id) === String(itemId)
    );
    if (item) {
      try {
        await updateQuantity(item.product_id, Number(item.quantity || 0) + 1);
      } catch (error) {
        refetchCart();
      }
    }
  };

  const handleDecrement = async (itemId) => {
    const item = cartItems.find(
      (cartItem) => String(cartItem.product_id) === String(itemId)
    );
    if (item) {
      try {
        await updateQuantity(item.product_id, Number(item.quantity || 1) - 1);
      } catch (error) {
        refetchCart();
      }
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await removeProduct(itemId);
    } catch (error) {
      refetchCart();
    }
  };

  // Keep the page fixed behind overlays so only the drawer content can scroll.
  useEffect(() => {
    document.body.classList.toggle("kuku-mobile-menu-open", click);

    if (click) {
      setIsAboutOpen(true);
    }

    return () => {
      document.body.classList.remove("kuku-mobile-menu-open");
    };
  }, [click]);
  useEffect(() => {
    const shouldLockScroll = click || searchBarShow || isCartOpen;

    if (!shouldLockScroll) {
      return undefined;
    }

    const scrollY = window.scrollY;
    const originalBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyStyles.overflow;
      document.body.style.position = originalBodyStyles.position;
      document.body.style.top = originalBodyStyles.top;
      document.body.style.left = originalBodyStyles.left;
      document.body.style.right = originalBodyStyles.right;
      document.body.style.width = originalBodyStyles.width;
      window.scrollTo(0, scrollY);
    };
  }, [click, isCartOpen, searchBarShow]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setClick(false);
        setIsAboutOpen(false);
        setSearchBarShow(false);
        setSearchContent(null);
        setIsCartOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    window.addEventListener(CART_DRAWER_OPEN_EVENT, openCart);

    return () => {
      window.removeEventListener(CART_DRAWER_OPEN_EVENT, openCart);
    };
  }, [openCart]);

  /* active link class — pink like the reference */
  const navLinkClass = ({ isActive }) =>
    `nav-link-item${isActive ? " nav-link-active" : ""}`;

  return (
    <nav className={`kuku-navbar${pathname === "/" ? " kuku-navbar--home" : ""}${click ? " kuku-navbar--menu-open" : ""}`}>
      <div className="kuku-navbar-inner">

        {/* ── Logo ── */}
        <Link to="/" className="kuku-brand" onClick={closeAll}>
          {logoData?.company_logo && (
            <img
              src={logoData.company_logo}
              alt="Kuku Namkeen"
              className="kuku-logo"
              width="120"
            />
          )}
          <img
            src="/Image/kuku-namkeen-logo.png"
            alt="Kuku Namkeen"
            className="kuku-logo"
            width="120"
          />
        </Link>

        {/* ── Mobile right-side icons + Hamburger ── */}
        <div className="kuku-mobile-right">
          {/* Cart icon — visible on mobile only */}
          <button
            type="button"
            className="kuku-icon-btn kuku-cart-btn kuku-mob-icon"
            onClick={openCart}
            aria-label="Cart"
          >
            <BsCart3 size={28} />
            <CartCount count={cartCount} />
          </button>

          {/* Profile icon — visible on mobile only */}
          {localStore ? (
            <AccountProfileLink
              className="kuku-mob-icon"
              onClick={handleProfileClick}
              size={30}
            />
          ) : (
            <Link to="/sign-up" className="kuku-icon-btn kuku-mob-icon" onClick={closeAll} aria-label="Account">
              <BsPersonCircle size={28} />
            </Link>
          )}

          {/* Hamburger toggle */}
          <button
            className="kuku-toggler"
            type="button"
            onClick={() => {
              setSearchBarShow(false);
              setSearchContent(null);
              setIsCartOpen(false);
              setClick(!click);
            }}
            aria-label="Toggle navigation"
          >
            {click ? (
              <X size={26} />
            ) : (
              <div className="kuku-hamburger">
                <span></span><span></span><span></span>
              </div>
            )}
          </button>
        </div>

        {/* ── Offcanvas Backdrop (mobile only) ── */}
        {click && (
          <div
            className="kuku-offcanvas-backdrop"
            onClick={closeAll}
            aria-hidden="true"
          />
        )}

        {/* ── Offcanvas Drawer (slides from RIGHT — matches reference offcanvas-end) ── */}
        <div className={`kuku-collapse${click ? " kuku-collapse-open" : ""}`}>

          {/* Offcanvas Header: "Kuku Foods" + close button */}
          <div className="kuku-offcanvas-header">
            <h5 className="kuku-offcanvas-title">Kuku Foods</h5>
            <button
              type="button"
              className="kuku-offcanvas-close"
              onClick={closeAll}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Offcanvas Body */}
          <div className="kuku-offcanvas-body">

            {/* Nav Links */}
            <ul className="kuku-nav">
              <li>
                <NavLink to="/" className={navLinkClass} onClick={closeAll} end>
                  Home
                </NavLink>
              </li>

              {/* About us dropdown */}
              <li
                className="kuku-dropdown-wrap"
                onMouseEnter={() => !click && setIsAboutOpen(true)}
                onMouseLeave={() => !click && setIsAboutOpen(false)}
              >
                <button
                  type="button"
                  className={`nav-link-item nav-dropdown-trigger${isAboutOpen ? " nav-link-active" : ""}`}
                  aria-expanded={isAboutOpen}
                  onClick={() => setIsAboutOpen((open) => !open)}
                >
                  About us <ChevronDown size={13} className={`nav-chevron${isAboutOpen ? " rotated" : ""}`} />
                </button>
                <div className={`kuku-dropdown-menu${isAboutOpen ? " open" : ""}`}>
                  <Link to="/about" className="kuku-dropdown-item" onClick={closeAll}>History</Link>
                  <Link to="/makingprocess" className="kuku-dropdown-item" onClick={closeAll}>Making Process</Link>
                </div>
              </li>

              <li>
                <NavLink to="/categories" className={navLinkClass} onClick={closeAll}>
                  Products
                </NavLink>
              </li>

              <li>
                <NavLink to="/become-distributor" className={navLinkClass} onClick={closeAll}>
                  Become Distributor
                </NavLink>
              </li>
              <li>
                <NavLink to="/blogs" className={navLinkClass} onClick={closeAll}>
                  Blogs
                </NavLink>
              </li>
              <li>
                <NavLink to="/corporate-gifting" className={navLinkClass} onClick={closeAll}>
                  Corporate Gifting
                </NavLink>
              </li>
              <li>
                <NavLink to="/track-orders" className={navLinkClass} onClick={closeAll}>
                  Track Order
                </NavLink>
              </li>
              <li>
                <NavLink to="/contact" className={navLinkClass} onClick={closeAll}>
                  Contact us
                </NavLink>
              </li>
            </ul>

            {/* Search form — matching reference offcanvas search */}
            <div className="kuku-offcanvas-search">
              <input
                type="search"
                placeholder="Search"
                className="kuku-offcanvas-search-input"
                onChange={onChange}
              />
              <button className="kuku-offcanvas-search-btn" type="button" onClick={toggleSearch}>
                Search
              </button>
            </div>

          </div>{/* /offcanvas-body */}

          {/* Right Icons (desktop only — hidden on mobile) */}
          <div className="kuku-nav-icons">
            {/* Search */}
            <button className="kuku-icon-btn" onClick={toggleSearch} aria-label="Search">
              <Search size={20} />
            </button>

            {/* Cart */}
            <button
              type="button"
              className="kuku-icon-btn kuku-cart-btn"
              onClick={openCart}
              aria-label="Cart"
            >
              <BsCart3 size={20} />
              <CartCount count={cartCount} />
            </button>

            {/* Account */}
            {localStore ? (
              <AccountProfileLink onClick={handleProfileClick} size={30} />
            ) : (
              <Link to="/sign-up" className="kuku-icon-btn" onClick={closeAll} aria-label="Account">
                <BsPersonCircle size={20} />
              </Link>
            )}
          </div>
        </div>

        {/* ── Search Overlay ── */}
        {searchBarShow && (
          <div className="kuku-search-overlay">
            <div className="kuku-search-box-wrap">
              <button onClick={toggleSearch} className="kuku-search-close">
                <X size={32} />
              </button>
              <div className="kuku-search-input-wrap">
                <Search size={22} className="kuku-search-icon" />
                <input
                  type="text"
                  placeholder="Search our store"
                  className="kuku-search-input"
                  autoFocus
                  onChange={onChange}
                />
              </div>
              {searchContent && (
                <div className="kuku-search-results">
                  <SearchContent searchContent={searchContent} />
                </div>
              )}
            </div>
          </div>
        )}

        <CartDrawer
          isOpen={isCartOpen}
          items={drawerItems}
          onClose={closeCart}
          onIncrement={handleIncrement}
          onDecrement={handleDecrement}
          onRemove={handleRemove}
          totalAmount={totalAmount}
          checkoutPath="/checkout"
        />
      </div>
    </nav>
  );
};

const Header = () => (
  <div className="fixed top-0 right-0 left-0 z-50">
    <HeaderData />
  </div>
);

export default Header;

